import { Settings } from 'luxon';
import { connect, ConnectedProps } from 'react-redux';
import { reset } from 'redux-form';

import NullState from 'dashboard/components-lib/NullState';
import { FormCurrentUserEmailData } from 'dashboard/forms/FormCurrentUserEmail';
import { FormCurrentUserPasswordData } from 'dashboard/forms/FormCurrentUserPassword';
import {
  FORM_CURRENT_USER_PERSONAL_DETAILS_NAME,
  FormCurrentUserPersonalDetailsData,
} from 'dashboard/forms/FormCurrentUserPersonalDetails';
import { FormCurrentUserProfilePictureData } from 'dashboard/forms/FormCurrentUserProfilePicture';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { User } from 'dashboard/models/user';
import { saveCurrentUser } from 'dashboard/redux/actions/current-user';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { rotateRecoveryCode } from 'dashboard/redux/thunk-actions/current-user';
import DashboardUserProfileView from 'dashboard/views/DashboardUserProfileView';
import { UserNotificationSettingsUpdateData } from 'dashboard/views/DashboardUserProfileView/UserNotificationSettings';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { ApiModel, ApiSingletonPayload } from 'lib/interfaces/api';

interface UserUpdatePasswordAttributes {
  password: string;
  'password-confirmation': string;
}

const DashboardUserProfileRoute = (props: PropsFromRedux) => {
  const updatePersonalDetails = async (attributes: FormCurrentUserPersonalDetailsData) => {
    props.clearMessages();

    try {
      const response = await SessionClientDashboard.updateResourceSingleton<User>({
        attributes,
        type: ApiModel.CurrentUser as const,
      });
      props.saveCurrentUser(response.data);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      Settings.defaultZone = response.data.attributes.timezone;

      props.showMessage('Your profile was updated', MessageType.Success);
    } catch (err) {
      props.showMessage('Please try again');
      props.reset(FORM_CURRENT_USER_PERSONAL_DETAILS_NAME);
    }
  };

  const startUpdateEmailAddress = async ({
    emailAddress,
    currentPassword,
  }: FormCurrentUserEmailData) => {
    props.clearMessages();

    try {
      const response = await SessionClientDashboard.postResourceSingletonAction<
        User,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ApiSingletonPayload<unknown, ApiModel.CurrentUser>
      >({ type: ApiModel.CurrentUser }, 'start-email-change', {
        meta: {
          'current-password': currentPassword,
          'email-address': emailAddress,
        },
      });
      props.saveCurrentUser(response.data);
      props.showMessage('Please confirm your new email.', MessageType.Success);
    } catch (err) {
      props.showMessage('Incorrect password.');
    }
  };

  const { callback: finishUpdateEmailAddress, error: finishUpdateEmailAddressError } =
    useApiCallback(async (confirmationToken: string) => {
      const { data } = await SessionClientDashboard.postResourceSingletonAction<
        User,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ApiSingletonPayload<unknown, ApiModel.CurrentUser>
      >(
        {
          type: ApiModel.CurrentUser,
        },
        'finish-email-change',
        {
          meta: {
            'confirmation-token': confirmationToken,
          },
        },
      );
      props.saveCurrentUser(data);

      props.showMessage('Your email has successfully been changed.', MessageType.Success);
    }, []);
  useApiErrorHandler({
    error: finishUpdateEmailAddressError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'Incorrect confirmation token provided':
          props.showMessage(
            'Your confirmation message is invalid. Please request another confirmation and try again.',
          );
          break;
        case 'Confirmation token expired':
          props.showMessage(
            'Your confirmation message has expired. Please request another confirmation and try again.',
          );
          break;
        default:
          props.showMessage('Failed update email address. Please try again.');
      }
    },
  });

  const updateProfilePicture = async ({ profilePicture }: FormCurrentUserProfilePictureData) => {
    props.clearMessages();

    try {
      const response = await SessionClientDashboard.updateResourceSingleton<User>(
        {
          attributes: {
            'profile-picture-upload': profilePicture,
          },
          type: ApiModel.CurrentUser as const,
        },
        {
          contentType: 'multipart/form-data',
        },
      );
      props.saveCurrentUser(response.data);
      props.showMessage('Your profile picture was updated.', MessageType.Success);
    } catch (err) {
      props.showMessage(
        'Failed to update profile picture. Please try again later.',
        MessageType.Failure,
      );
    }
  };

  const updatePassword = async ({
    currentPassword,
    password,
    passwordConfirmation,
  }: FormCurrentUserPasswordData) => {
    props.clearMessages();

    try {
      const response = await SessionClientDashboard.updateResourceSingleton<
        User,
        ApiSingletonPayload<UserUpdatePasswordAttributes, ApiModel.CurrentUser>,
        { 'current-password': string }
      >(
        {
          attributes: {
            password,
            'password-confirmation': passwordConfirmation,
          },
          type: ApiModel.CurrentUser as const,
        },
        {
          meta: {
            'current-password': currentPassword,
          },
        },
      );
      props.saveCurrentUser(response.data);
      props.showMessage('Your password was updated.', MessageType.Success);
    } catch (err) {
      if (err instanceof ApiResponseError) {
        switch (err.status) {
          case 401:
            props.showMessage('Incorrect password.');
            return;
          case 422:
            props.showMessage('Please select another password.');
            return;
          default:
            props.showMessage('Failed to update password. Please try again.');
            return;
        }
      } else {
        props.showMessage('We are experiencing network issues. Please try again.');
      }
    }
  };

  const {
    callback: setupMfaEmail,
    error: setupMfaEmailError,
    loading: setupMfaEmailLoading,
  } = useApiCallback(async () => {
    const { data } = await SessionClientDashboard.postResourceSingletonAction<User>(
      { type: ApiModel.CurrentUser },
      'setup-mfa-email',
    );
    props.saveCurrentUser(data);
    props.showMessage(
      'Please check your email to finish setting up 2-factor authentication.',
      MessageType.Success,
    );
  }, []);
  useApiErrorHandler({
    error: setupMfaEmailError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'User has already enabled email MFA':
          props.showMessage('You have already enabled email 2-factor authentication.');
          break;
        default:
          props.showMessage('Failed to setup 2-factor authentication. Please try again.');
      }
    },
  });

  const {
    callback: enableMfaEmail,
    error: enableMfaEmailError,
    loading: enableMfaEmailLoading,
  } = useApiCallback(async (confirmationToken: string) => {
    const { data } = await SessionClientDashboard.postResourceSingletonAction<
      User,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ApiSingletonPayload<unknown, ApiModel.CurrentUser>,
      { 'confirmation-token': string }
    >(
      {
        type: ApiModel.CurrentUser,
      },
      'enable-mfa-email',
      {
        meta: {
          'confirmation-token': confirmationToken,
        },
      },
    );
    props.saveCurrentUser(data);

    props.showMessage(
      'Success. Email 2-factor authentication has been enabled.',
      MessageType.Success,
    );
  }, []);
  useApiErrorHandler({
    error: enableMfaEmailError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'Incorrect confirmation token provided':
          props.showMessage(
            'Your confirmation message is invalid. Please request another confirmation and try again.',
          );
          break;
        case 'Confirmation token expired':
          props.showMessage(
            'Your confirmation message has expired. Please request another confirmation and try again.',
          );
          break;
        default:
          props.showMessage('Failed to enable 2-factor authentication. Please try again.');
      }
    },
  });

  const {
    callback: disableMfaEmail,
    error: disableMfaEmailError,
    loading: disableMfaEmailLoading,
  } = useApiCallback(async () => {
    const { data } = await SessionClientDashboard.postResourceSingletonAction<
      User,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ApiSingletonPayload<unknown, ApiModel.CurrentUser>
    >(
      {
        type: ApiModel.CurrentUser,
      },
      'disable-mfa-email',
    );
    props.saveCurrentUser(data);

    props.showMessage(
      'Success. Email 2-factor authentication has been disabled.',
      MessageType.Success,
    );
  }, []);
  useApiErrorHandler({
    error: disableMfaEmailError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: () =>
      props.showMessage('Failed to disable 2-factor authentication. Please try again.'),
  });

  const {
    callback: setupMfaPhone,
    error: setupMfaPhoneError,
    loading: setupMfaPhoneLoading,
  } = useApiCallback(async () => {
    const { data } = await SessionClientDashboard.postResourceSingletonAction<User>(
      { type: ApiModel.CurrentUser },
      'setup-mfa-phone',
    );
    props.saveCurrentUser(data);
    props.showMessage(
      'Please check your phone to finish setting up 2-factor authentication.',
      MessageType.Success,
    );
  }, []);
  useApiErrorHandler({
    error: setupMfaPhoneError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      if (e.errors?.[0].title === 'Phone number unsubscribed') {
        return props.showMessage(
          'The provided number is opted out from receiving messages from us. Please try a different number.',
        );
      }
      if (e.errors?.[0].title === 'Rate limit exceeded') {
        return props.showMessage(
          'You are sending too many messages. Please try again in several minutes.',
        );
      }
      switch (e.errors?.[0].details) {
        case 'Phone number must be present to enable phone number MFA':
          props.showMessage(
            'You must have a phone number linked to your account to setup phone 2-factor authentication.',
          );
          break;
        case 'User has already enabled phone MFA':
          props.showMessage('You have already enabled phone 2-factor authentication.');
          break;
        default:
          props.showMessage('Failed to setup 2-factor authentication. Please try again.');
      }
    },
  });

  const {
    callback: enableMfaPhone,
    error: enableMfaPhoneError,
    loading: enableMfaPhoneLoading,
  } = useApiCallback(async (confirmationCode: string) => {
    const { data } = await SessionClientDashboard.postResourceSingletonAction<
      User,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ApiSingletonPayload<unknown, ApiModel.CurrentUser>,
      { 'confirmation-code': string }
    >(
      {
        type: ApiModel.CurrentUser,
      },
      'enable-mfa-phone',
      {
        meta: {
          'confirmation-code': confirmationCode,
        },
      },
    );
    props.saveCurrentUser(data);

    props.showMessage(
      'Success. Phone 2-factor authentication has been enabled.',
      MessageType.Success,
    );
  }, []);
  useApiErrorHandler({
    error: enableMfaPhoneError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'Incorrect confirmation code provided':
          props.showMessage(
            'Your confirmation code is invalid. Please request another confirmation and try again.',
          );
          break;
        case 'Confirmation code expired':
          props.showMessage(
            'Your confirmation code has expired. Please request another confirmation and try again.',
          );
          break;
        default:
          props.showMessage('Failed to enable 2-factor authentication. Please try again.');
      }
    },
  });

  const {
    callback: disableMfaPhone,
    error: disableMfaPhoneError,
    loading: disableMfaPhoneLoading,
  } = useApiCallback(async () => {
    const { data } = await SessionClientDashboard.postResourceSingletonAction<
      User,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ApiSingletonPayload<unknown, ApiModel.CurrentUser>
    >(
      {
        type: ApiModel.CurrentUser,
      },
      'disable-mfa-phone',
    );
    props.saveCurrentUser(data);

    props.showMessage(
      'Success. Phone 2-factor authentication has been disabled.',
      MessageType.Success,
    );
  }, []);
  useApiErrorHandler({
    error: disableMfaPhoneError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: () =>
      props.showMessage('Failed to disable 2-factor authentication. Please try again.'),
  });

  const {
    callback: setupMfaTotp,
    error: setupMfaTotpError,
    loading: setupMfaTotpLoading,
  } = useApiCallback(async () => {
    const { data } = await SessionClientDashboard.postResourceSingletonAction<User>(
      { type: ApiModel.CurrentUser },
      'setup-mfa-totp',
    );
    props.saveCurrentUser(data);
    props.showMessage(
      'Please use your TOTP app to finish setting up 2-factor authentication.',
      MessageType.Success,
    );
  }, []);
  useApiErrorHandler({
    error: setupMfaTotpError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'User has already enabled TOTP MFA':
          props.showMessage('You have already enabled TOTP 2-factor authentication.');
          break;
        default:
          props.showMessage('Failed to setup 2-factor authentication. Please try again.');
      }
    },
  });

  const {
    callback: enableMfaTotp,
    error: enableMfaTotpError,
    loading: enableMfaTotpLoading,
  } = useApiCallback(async (confirmationCode: string) => {
    const { data } = await SessionClientDashboard.postResourceSingletonAction<
      User,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ApiSingletonPayload<unknown, ApiModel.CurrentUser>,
      { totp: string }
    >(
      {
        type: ApiModel.CurrentUser,
      },
      'enable-mfa-totp',
      {
        meta: {
          totp: confirmationCode,
        },
      },
    );
    props.saveCurrentUser(data);

    props.showMessage(
      'Success. Totp 2-factor authentication has been enabled.',
      MessageType.Success,
    );
  }, []);
  useApiErrorHandler({
    error: enableMfaTotpError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'Incorrect TOTP code provided':
          props.showMessage('Your provided code is invalid. Please try again.');
          break;
        default:
          props.showMessage('Failed to enable 2-factor authentication. Please try again.');
      }
    },
  });

  const {
    callback: disableMfaTotp,
    error: disableMfaTotpError,
    loading: disableMfaTotpLoading,
  } = useApiCallback(async () => {
    const { data } = await SessionClientDashboard.postResourceSingletonAction<
      User,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ApiSingletonPayload<unknown, ApiModel.CurrentUser>
    >(
      {
        type: ApiModel.CurrentUser,
      },
      'disable-mfa-totp',
    );
    props.saveCurrentUser(data);

    props.showMessage(
      'Success. TOTP 2-factor authentication has been disabled.',
      MessageType.Success,
    );
  }, []);
  useApiErrorHandler({
    error: disableMfaTotpError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: () =>
      props.showMessage('Failed to disable 2-factor authentication. Please try again.'),
  });

  const {
    callback: rotateRecoveryCode,
    error: rotateRecoveryCodeError,
    loading: rotateRecoveryCodeLoading,
  } = useApiCallback(async () => {
    await props.rotateRecoveryCode();
  }, []);
  useApiErrorHandler({
    error: rotateRecoveryCodeError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: () => props.showMessage('Failed to rotate recovery code. Please try again.'),
  });

  const updateUserNotificationSettings = async ({
    userNotificationPolicy,
  }: UserNotificationSettingsUpdateData) => {
    props.clearMessages();

    try {
      const { data } = await SessionClientDashboard.updateResourceSingleton<User>({
        attributes: {
          'notification-policy': userNotificationPolicy,
        },
        type: ApiModel.CurrentUser as const,
      });
      props.saveCurrentUser(data);
      props.showMessage('Successfully updated your notifications.', MessageType.Success);
    } catch (err) {
      props.showMessage(
        'Failed to change notification settings. Please try again later.',
        MessageType.Failure,
      );
    }
  };

  if (props.currentUser == null) {
    return (
      <Backdrop>
        <NullState
          message='We were unable to load your user details.'
          backRoute='/dashboard/home'
        />
      </Backdrop>
    );
  }

  return (
    <DashboardUserProfileView
      currentUser={props.currentUser}
      organization={props.organization}
      startUpdateEmailAddress={startUpdateEmailAddress}
      finishUpdateEmailAddress={finishUpdateEmailAddress}
      updatePassword={updatePassword}
      updatePersonalDetails={updatePersonalDetails}
      updateProfilePicture={updateProfilePicture}
      setupMfaEmail={setupMfaEmail}
      enableMfaEmail={enableMfaEmail}
      disableMfaEmail={disableMfaEmail}
      mfaEmailLoading={setupMfaEmailLoading || enableMfaEmailLoading || disableMfaEmailLoading}
      setupMfaPhone={setupMfaPhone}
      enableMfaPhone={enableMfaPhone}
      disableMfaPhone={disableMfaPhone}
      mfaPhoneLoading={setupMfaPhoneLoading || enableMfaPhoneLoading || disableMfaPhoneLoading}
      setupMfaTotp={setupMfaTotp}
      enableMfaTotp={enableMfaTotp}
      disableMfaTotp={disableMfaTotp}
      mfaTotpLoading={setupMfaTotpLoading || enableMfaTotpLoading || disableMfaTotpLoading}
      recoveryCode={props.recoveryCode}
      rotateRecoveryCode={rotateRecoveryCode}
      rotateRecoveryCodeLoading={rotateRecoveryCodeLoading}
      featureFlags={props.featureFlags}
      updateUserNotificationSettings={updateUserNotificationSettings}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: state.currentUser.currentUser,
    featureFlags: state.featureFlags.featureFlags,
    organization: state.organization.organization,
    recoveryCode: state.currentUser.recoveryCode,
  };
};

const mapDispatchToProps = {
  saveCurrentUser,
  clearMessages,
  showMessage,
  rotateRecoveryCode,
  reset,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardUserProfileRoute);
